import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Card, Descriptions, Input, message, Select, Space, Table } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { DocumentPrintSheet } from "../../components/DocumentPrintSheet";
import { QtyInput } from "../../components/QtyInput";
import { MoneyInput } from "../../components/MoneyInput";
import { SiteLabel } from "../../components/SiteLabel";
import { DocLink } from "../../components/DocLink";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useSuppliersQuery, useItemsQuery, useTaxPoliciesQuery } from "../../helpers/queries";
import { fmtMVR } from "../../helpers/format";
import type { PurchaseOrder, PurchaseRequisition } from "../../api-models";

interface POLine {
  key: string;
  itemId?: number;
  qty?: number;
  unitPriceLaari?: number;
  // prItemId links this line back to the PurchaseRequisitionItem it fulfils, carrying its
  // MRQ/allocation/direct-delivery-site link forward onto the PurchaseOrderItem (spec
  // §14.1/§16) — set when the PO was opened from a PR (?prId=) rather than typed ad hoc.
  prItemId?: number;
  // id is the existing PurchaseOrderItem this line edits. The PO line does not store the
  // prItemId it was created from, so echoing the id back is what keeps the allocation
  // trace alive across a re-save of a draft PO. Undefined for lines added here.
  id?: number;
  // The tax policy this line is priced under. Undefined means "not decided here yet", and
  // the material master's own policy is used (see effectiveTaxPolicy) - which is also what
  // makes a line added before the reference data arrives still pick its default up. null
  // is a policy the buyer has cleared, and is sent as null so the server leaves the line
  // untaxed instead of applying the master default behind them.
  taxPolicyId?: number | null;
}

// A charge line is a cost on the order that is not material - freight, handling, a
// clearing fee - so it is typed rather than picked from the master, and having no material
// behind it there is no default tax to inherit: undefined and null both mean untaxed.
interface POCharge {
  key: string;
  description?: string;
  qty?: number;
  unitPriceLaari?: number;
  taxPolicyId?: number | null;
}

export default function PODetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: suppliers = [] } = useSuppliersQuery();
  const { data: items = [] } = useItemsQuery();
  const { data: taxPolicies = [] } = useTaxPoliciesQuery();

  const isNew = id === "new";
  const prId = searchParams.get("prId");
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<POLine[]>([]);
  const [charges, setCharges] = useState<POCharge[]>([]);
  const [supplierId, setSupplierId] = useState<number>();
  const [terms, setTerms] = useState("");
  const [tolerancePct, setTolerancePct] = useState(0);

  async function load() {
    if (isNew) {
      if (!prId) return;
      const pr = await request<PurchaseRequisition>({ url: `/prs/${prId}` });
      setSupplierId(undefined);
      setLines(
        (pr.items ?? []).map((it) => ({
          key: String(it.id), itemId: it.itemId, qty: it.qty, unitPriceLaari: it.estUnitPriceLaari ?? 0, prItemId: it.id,
        })),
      );
      setCharges([]);
      return;
    }
    const res = await request<PurchaseOrder>({ url: `/pos/${id}` });
    setPo(res);
    setLines(
      (res.items ?? []).map((it) => ({
        key: String(it.id), id: it.id, itemId: it.itemId, qty: it.qty, unitPriceLaari: it.unitPriceLaari,
        taxPolicyId: it.taxPolicyId ?? null,
      })),
    );
    setCharges(
      (res.charges ?? []).map((c) => ({
        key: String(c.id), description: c.description, qty: c.qty, unitPriceLaari: c.unitPriceLaari,
        taxPolicyId: c.taxPolicyId ?? null,
      })),
    );
    setSupplierId(res.supplierId);
    setTerms(res.terms);
    setTolerancePct(res.tolerancePct);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, prId]);

  const canWrite = hasAnyGrant(user, "PO.Write");
  const editable = isNew || po?.status === "DRAFT";

  // A line starts on its material's own tax policy; the buyer can change it or clear it.
  // The amount is worked out here exactly as the server works it out on save
  // (procurement.go's lineTaxLaari), so the running totals match what is stored.
  function effectiveTaxPolicy(line: POLine): number | null {
    if (line.taxPolicyId !== undefined) return line.taxPolicyId;
    return items.find((m) => m.id === line.itemId)?.taxPolicyId ?? null;
  }
  function lineTax(line: POLine): number {
    const pct = taxPolicies.find((p) => p.id === effectiveTaxPolicy(line))?.percentage ?? 0;
    return Math.round(((line.qty ?? 0) * (line.unitPriceLaari ?? 0) * pct) / 100);
  }
  // A charge has no material behind it, so its tax is whatever policy was chosen on the
  // line and nothing more.
  function chargeTax(charge: POCharge): number {
    const pct = taxPolicies.find((p) => p.id === charge.taxPolicyId)?.percentage ?? 0;
    return Math.round(((charge.qty ?? 0) * (charge.unitPriceLaari ?? 0) * pct) / 100);
  }
  // Charges are money committed to the supplier just as material is, so they sit in the
  // same subtotal, tax and grand total the server stores and the approval threshold reads.
  const subtotal =
    lines.reduce((sum, l) => sum + (l.qty ?? 0) * (l.unitPriceLaari ?? 0), 0) +
    charges.reduce((sum, c) => sum + (c.qty ?? 0) * (c.unitPriceLaari ?? 0), 0);
  const taxTotal = lines.reduce((sum, l) => sum + lineTax(l), 0) + charges.reduce((sum, c) => sum + chargeTax(c), 0);

  async function save() {
    const payload = {
      supplierId,
      prId: isNew && prId ? Number(prId) : po?.prId,
      terms,
      notes: "",
      tolerancePct,
      items: lines
        .filter((l) => l.itemId && l.qty)
        .map((l) => ({
          id: l.id, prItemId: l.prItemId, itemId: l.itemId, qty: l.qty, unitPriceLaari: l.unitPriceLaari ?? 0,
          // Always sent, never omitted: an absent key means "inherit the master default",
          // which would put back a tax the buyer has just taken off the line.
          taxPolicyId: effectiveTaxPolicy(l),
        })),
      charges: charges
        .filter((c) => c.description?.trim() && c.qty)
        .map((c) => ({
          description: c.description!.trim(), qty: c.qty, unitPriceLaari: c.unitPriceLaari ?? 0,
          taxPolicyId: c.taxPolicyId ?? null,
        })),
    };
    if (isNew) {
      const created = await request<PurchaseOrder>({ method: "POST", url: "/pos", data: payload });
      message.success("Purchase order created.");
      navigate(`/procurement/pos/${created.id}`, { replace: true });
    } else {
      await request({ method: "PUT", url: `/pos/${id}`, data: payload });
      message.success("Purchase order updated.");
      load();
    }
  }

  async function act(action: string) {
    await request({ method: "POST", url: `/pos/${id}/${action}` });
    message.success("Done.");
    load();
  }

  if (!isNew && !po) return null;

  return (
    <div>
      <PageHeader
        title={isNew ? "New Purchase Order" : po!.docNo}
        extra={
          <Space className="glut-no-print">
            {po && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={po.status} />
              </span>
            )}
            {editable && canWrite && (
              <Button type="primary" onClick={save} loading={loading} data-tour="po-save">
                Save
              </Button>
            )}
            {!isNew && po?.status === "DRAFT" && canWrite && (
              <Button onClick={() => act("submit")} loading={loading} data-tour="po-submit">
                Submit for approval
              </Button>
            )}
            {!isNew && (po?.status === "DRAFT" || po?.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={() => act("cancel")} loading={loading}>
                Cancel
              </Button>
            )}
            {/* A direct-delivery PO (directDeliverySiteId set) is received via a site MRN
                (POST /api/mrns, sourceType=PO) rather than a GRN. That one flow is still
                API-only: the other sources are received through the ReceiveDialog on their
                own dispatch document (a GIN, a transfer note, a GIN Direct). */}
            {!isNew && po?.directDeliverySiteId == null && (po?.status === "APPROVED" || po?.status === "PARTIALLY_RECEIVED") && hasAnyGrant(user, "GRN.Write") && (
              <Button onClick={() => navigate(`/procurement/grns/new?poId=${id}`)} data-tour="po-record-receipt">
                Record receipt
              </Button>
            )}
            {/* The other way a non-direct-delivery order can arrive: the supplier drops it
                straight at the site whose request it was bought for. That sends it to the
                site rather than stocking CW, so it is its own document with its own
                authority (GINDirect.Write), and the site still posts an MRN when it lands
                — the screen behind this button shows which lines, if any, qualify. */}
            {!isNew && po?.directDeliverySiteId == null && (po?.status === "APPROVED" || po?.status === "PARTIALLY_RECEIVED") && hasAnyGrant(user, "GINDirect.Write") && (
              <Button onClick={() => navigate(`/procurement/gin-directs/new?poId=${id}`)}>Deliver to site (GIN Direct)</Button>
            )}
            {!isNew && (po?.status === "APPROVED" || po?.status === "PARTIALLY_RECEIVED") && canWrite && (
              <Button onClick={() => navigate(`/procurement/pos/${id}/amendments/new`)} data-tour="po-amend">
                Amend
              </Button>
            )}
            {!isNew && <Button onClick={() => window.print()}>Print</Button>}
          </Space>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }} className="glut-no-print">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Supplier">
            {editable ? (
              <Select
                data-tour="po-supplier"
                showSearch
                style={{ width: 240 }}
                options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
                value={supplierId}
                onChange={setSupplierId}
              />
            ) : (
              po!.supplier?.name
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Terms">
            {editable ? <Input value={terms} onChange={(e) => setTerms(e.target.value)} /> : po!.terms}
          </Descriptions.Item>
          <Descriptions.Item label="Tolerance %">
            {editable ? (
              <QtyInput value={tolerancePct} onChange={(v) => setTolerancePct(v ?? 0)} />
            ) : (
              `${po!.tolerancePct}%`
            )}
          </Descriptions.Item>
          {!isNew && po?.prId != null && (
            <Descriptions.Item label="Requisition">
              <DocLink docType="PR" id={po.prId} docNo={po.pr?.docNo} />
            </Descriptions.Item>
          )}
          {!isNew && po?.directDeliverySiteId != null && (
            <Descriptions.Item label="Direct delivery">
              <SiteLabel site={po.directDeliverySite} fallback={`Site #${po.directDeliverySiteId}`} />
            </Descriptions.Item>
          )}
          {!isNew && po?.currency && po.currency !== "MVR" && (
            <Descriptions.Item label="Currency">
              {po.currency} @ {po.exchangeRate}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
      <Card size="small" title="Items" className="glut-no-print" data-tour="po-lines">
        <Table<POLine>
          rowKey="key"
          pagination={false}
          size="small"
          dataSource={lines}
          columns={[
            {
              title: "Item",
              render: (_, row) =>
                editable ? (
                  <Select
                    showSearch
                    style={{ width: 240 }}
                    value={row.itemId}
                    options={items.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                    filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                    onChange={(v) =>
                      setLines((prev) =>
                        // Dropping the line's own choice re-derives the default from the
                        // material just picked.
                        prev.map((l) => (l.key === row.key ? { ...l, itemId: v, taxPolicyId: undefined } : l)),
                      )
                    }
                  />
                ) : (
                  items.find((m) => m.id === row.itemId)?.name
                ),
            },
            {
              title: "Qty",
              render: (_, row) =>
                editable ? (
                  <QtyInput value={row.qty} onChange={(v) => setLines((prev) => prev.map((l) => (l.key === row.key ? { ...l, qty: v ?? undefined } : l)))} />
                ) : (
                  row.qty
                ),
            },
            {
              title: "Unit price",
              render: (_, row) =>
                editable ? (
                  <MoneyInput valueLaari={row.unitPriceLaari} onChangeLaari={(v) => setLines((prev) => prev.map((l) => (l.key === row.key ? { ...l, unitPriceLaari: v } : l)))} />
                ) : (
                  fmtMVR(row.unitPriceLaari)
                ),
            },
            {
              title: "Tax",
              render: (_, row) =>
                editable ? (
                  <Select
                    style={{ width: 200 }}
                    value={effectiveTaxPolicy(row)}
                    allowClear
                    placeholder="No tax"
                    options={taxPolicies.map((p) => ({ value: p.id, label: `${p.name} (${p.percentage}%)` }))}
                    onChange={(v) => setLines((prev) => prev.map((l) => (l.key === row.key ? { ...l, taxPolicyId: v ?? null } : l)))}
                  />
                ) : (
                  taxPolicies.find((p) => p.id === effectiveTaxPolicy(row))?.name ?? "-"
                ),
            },
            { title: "Tax amount", render: (_, row) => fmtMVR(lineTax(row)) },
            { title: "Line total", render: (_, row) => fmtMVR((row.qty ?? 0) * (row.unitPriceLaari ?? 0) + lineTax(row)) },
            ...(editable
              ? [
                  {
                    title: "",
                    render: (_: unknown, row: POLine) => (
                      <Button icon={<DeleteOutlined />} danger type="text" onClick={() => setLines((prev) => prev.filter((l) => l.key !== row.key))} />
                    ),
                  },
                ]
              : []),
          ]}
        />
        {editable && (
          <Button icon={<PlusOutlined />} style={{ marginTop: 8 }} onClick={() => setLines((prev) => [...prev, { key: `new-${Date.now()}` }])}>
            Add item
          </Button>
        )}
      </Card>
      {/* Costs the supplier bills that are not material and have no Material Master record
          - freight, handling, clearing. A settled order with none of them shows nothing. */}
      {(editable || charges.length > 0) && (
        <Card size="small" title="Other charges" className="glut-no-print" style={{ marginTop: 16 }}>
          <Table<POCharge>
            rowKey="key"
            pagination={false}
            size="small"
            dataSource={charges}
            locale={{ emptyText: "No additional charges." }}
            columns={[
              {
                title: "Description",
                render: (_, row) =>
                  editable ? (
                    <Input
                      style={{ width: 240 }}
                      placeholder="e.g. Freight"
                      value={row.description}
                      onChange={(e) => setCharges((prev) => prev.map((c) => (c.key === row.key ? { ...c, description: e.target.value } : c)))}
                    />
                  ) : (
                    row.description
                  ),
              },
              {
                title: "Qty",
                render: (_, row) =>
                  editable ? (
                    <QtyInput value={row.qty} onChange={(v) => setCharges((prev) => prev.map((c) => (c.key === row.key ? { ...c, qty: v ?? undefined } : c)))} />
                  ) : (
                    row.qty
                  ),
              },
              {
                title: "Unit price",
                render: (_, row) =>
                  editable ? (
                    <MoneyInput valueLaari={row.unitPriceLaari} onChangeLaari={(v) => setCharges((prev) => prev.map((c) => (c.key === row.key ? { ...c, unitPriceLaari: v } : c)))} />
                  ) : (
                    fmtMVR(row.unitPriceLaari)
                  ),
              },
              {
                title: "Tax",
                render: (_, row) =>
                  editable ? (
                    <Select
                      style={{ width: 200 }}
                      value={row.taxPolicyId ?? null}
                      allowClear
                      placeholder="No tax"
                      options={taxPolicies.map((p) => ({ value: p.id, label: `${p.name} (${p.percentage}%)` }))}
                      onChange={(v) => setCharges((prev) => prev.map((c) => (c.key === row.key ? { ...c, taxPolicyId: v ?? null } : c)))}
                    />
                  ) : (
                    taxPolicies.find((p) => p.id === row.taxPolicyId)?.name ?? "-"
                  ),
              },
              { title: "Tax amount", render: (_, row) => fmtMVR(chargeTax(row)) },
              { title: "Line total", render: (_, row) => fmtMVR((row.qty ?? 0) * (row.unitPriceLaari ?? 0) + chargeTax(row)) },
              ...(editable
                ? [
                    {
                      title: "",
                      render: (_: unknown, row: POCharge) => (
                        <Button icon={<DeleteOutlined />} danger type="text" onClick={() => setCharges((prev) => prev.filter((c) => c.key !== row.key))} />
                      ),
                    },
                  ]
                : []),
            ]}
          />
          {editable && (
            <Button icon={<PlusOutlined />} style={{ marginTop: 8 }} onClick={() => setCharges((prev) => [...prev, { key: `new-charge-${Date.now()}`, qty: 1 }])}>
              Add charge
            </Button>
          )}
        </Card>
      )}
      {/* A saved order shows the figures the server stored; a draft being edited shows the
          same three computed from the items and charges on screen. */}
      <Card size="small" className="glut-no-print" style={{ marginTop: 16 }}>
        <div style={{ textAlign: "right" }}>
          <div>Subtotal: {fmtMVR(po && !editable ? po.subtotalLaari : subtotal)}</div>
          <div>Tax: {fmtMVR(po && !editable ? po.taxLaari : taxTotal)}</div>
          <div style={{ fontWeight: 600 }}>Grand total: {fmtMVR(po && !editable ? po.totalLaari : subtotal + taxTotal)}</div>
        </div>
      </Card>
      {!isNew && <ApprovalTimeline docType="PO" docId={Number(id)} refreshKey={po?.status} onActed={load} />}
      {!isNew && po && (
        <DocumentPrintSheet
          title="Purchase Order"
          docNo={po.docNo}
          meta={[
            { label: "Supplier", value: po.supplier?.name ?? "" },
            { label: "Terms", value: po.terms },
            { label: "Subtotal", value: fmtMVR(po.subtotalLaari) },
            { label: "Tax", value: fmtMVR(po.taxLaari) },
            { label: "Grand total", value: fmtMVR(po.totalLaari) },
          ]}
        >
          <Table
            rowKey="id"
            dataSource={po.items ?? []}
            pagination={false}
            columns={[
              { title: "Code", render: (_, r) => r.item?.code },
              { title: "Item", render: (_, r) => r.item?.name },
              { title: "Qty", dataIndex: "qty" },
              { title: "Unit price", dataIndex: "unitPriceLaari", render: (v) => fmtMVR(v) },
              { title: "Tax", render: (_, r) => (r.taxPolicy ? `${r.taxPolicy.name} (${r.taxPercentage}%)` : "-") },
              { title: "Tax amount", render: (_, r) => fmtMVR(r.taxLaari) },
              { title: "Total", render: (_, r) => fmtMVR(r.qty * r.unitPriceLaari + r.taxLaari) },
            ]}
          />
          {(po.charges ?? []).length > 0 && (
            <>
              <h4 style={{ marginTop: 16 }}>Other charges</h4>
              <Table
                rowKey="id"
                dataSource={po.charges ?? []}
                pagination={false}
                columns={[
                  { title: "Description", dataIndex: "description" },
                  { title: "Qty", dataIndex: "qty" },
                  { title: "Unit price", dataIndex: "unitPriceLaari", render: (v) => fmtMVR(v) },
                  { title: "Tax", render: (_, r) => (r.taxPolicy ? `${r.taxPolicy.name} (${r.taxPercentage}%)` : "-") },
                  { title: "Tax amount", render: (_, r) => fmtMVR(r.taxLaari) },
                  { title: "Total", render: (_, r) => fmtMVR(r.qty * r.unitPriceLaari + r.taxLaari) },
                ]}
              />
            </>
          )}
        </DocumentPrintSheet>
      )}
    </div>
  );
}
